import React, { Fragment, type CSSProperties } from 'react';
import { flexRender, type Row } from '@tanstack/react-table';

import { getStylesForTable } from 'components/partials/table-component/helpers/get-styles-for-table';
import { getExpandedRowStyles } from 'components/partials/table-component/helpers/get-expanded-row-styles';

const DEPTH_INDENT_PX = 16;
const BASE_PADDING_PX = 12;

interface TableRowProps {
  row: Row<unknown>;
  rowInd: number;
  rowsLength: number;
  expandable: boolean;
  renderSubComponent?: (props: { row: Row<unknown> }) => React.ReactNode;
}

const TableRow = ({
  row,
  rowInd,
  rowsLength,
  expandable,
  renderSubComponent,
}: TableRowProps): React.JSX.Element => {
  const visibleCells = row.getVisibleCells();
  const lastCellInd = visibleCells.length - 1;
  const isExpanded = expandable && row.getIsExpanded();
  const hasSubComponent = Boolean(renderSubComponent && isExpanded);
  const depthPadding = expandable ? row.depth * DEPTH_INDENT_PX : 0;

  const getCellStyles = (cellInd: number): string =>
    expandable
      ? getExpandedRowStyles(rowInd, cellInd, rowsLength, lastCellInd, hasSubComponent)
      : getStylesForTable(rowInd, cellInd, rowsLength, lastCellInd);

  const getCellPadding = (cellInd: number): CSSProperties => {
    const isFirstCell = cellInd === 0;

    if (isFirstCell && depthPadding > 0) {
      return { paddingLeft: `${depthPadding + BASE_PADDING_PX}px` };
    }

    return {};
  };

  return (
    <Fragment key={row.id}>
      <tr
        className="p-4"
        data-testid={`table-row-${rowInd}`}
        data-expanded={isExpanded}
        data-depth={row.depth}
      >
        {visibleCells.map((cell, cellInd) => (
          <td
            className={`${cell.column.columnDef.className} border border-navy-200 ${getCellStyles(cellInd)}`}
            key={cell.id}
            data-testid={`table-cell-${rowInd}-${cellInd}`}
            style={getCellPadding(cellInd)}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
      {hasSubComponent && (
        <tr
          className="bg-gray-500"
          data-testid={`table-row-${rowInd}-subcomponent`}
        >
          <td
            colSpan={visibleCells.length}
            className="border border-t-0 border-navy-200 p-4"
          >
            {renderSubComponent?.({ row })}
          </td>
        </tr>
      )}
    </Fragment>
  );
};

export default TableRow;
